/**
 * Generate tour steps for the main navigation component
 * @param {Object} params - Configuration parameters
 * @param {boolean} params.isConsumer - Whether the user is a consumer
 * @param {boolean} params.isOwner - Whether the user is an owner
 * @param {boolean} params.isImageViewer - Whether the user is an image viewer
 * @param {boolean} params.isDispatcher - Whether the user is a dispatcher
 * @param {Function} params.openAndLockMenu - Callback to open and lock the menu
 * @param {Function} params.closeAndUnlockMenu - Callback to close and unlock the menu
 * @returns {Array} Array of tour steps
 */

// Targets without CSS selector prefix (for general use)
export const tourStepsTargets = {
  devicesLink: 'devicesLink',
  helpLink: 'helpLink',
  appUsersLink: 'appUsersLink',
  connectionsLink: 'connectionsLink',
  eventsLink: 'eventsLink',
  accountsLink: 'accountsLink',
  kabobMenu: 'kabobMenu',
  downloadsLink: 'downloadsLink',
  instructionsLink: 'instructionsLink',
  trainingLink: 'trainingLink',
  trainingScenariosLink: 'trainingScenariosLink',
  nonRMSEventsLink: 'nonRMSEventsLink',
  configurationLink: 'configurationLink',
  conditionalUsersLink: 'conditionalUsersLink',
  subgroupsLink: 'subgroupsLink',
  summaryLink: 'summaryLink',
  dispatcherLink: 'dispatcherLink',
  cadLink: 'cadLink',
  ssoLink: 'ssoLink',
  viewYourAccountLink: 'viewYourAccountLink',
  changePasswordLink: 'changePasswordLink',
  supportLink: 'supportLink',
  logoutLink: 'logoutLink',
  welcomeLink: 'welcomeLink',
};

export function generateTourSteps({
  isConsumer,
  isOwner,
  isImageViewer,
  isDispatcher,
  openAndLockMenu,
  closeAndUnlockMenu,
}) {
  // Selectors with CSS selector prefix (for tour steps)
  const selector = (target) => `.${target}`;

  const defaultParams = {
    enableScrolling: false,
  };
  const placementLeftParams = {
    placement: 'left',
    enableScrolling: false,
  };
  const tourObj = {
    componentName: 'mainNavigation',
    newSteps: [
      {
        target: selector(tourStepsTargets.helpLink),
        content: `Welcome to the Sentry-SECURE Portal<br><br>
         Sentry-SECURE platform provides situational awareness for connected RMS Owner and Consumer Organizations in a cloud platform<br><br>
         Help prompts like this one are located on all tabs and will provide contextual information`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.devicesLink),
        content: `Manage Sentry-RMS units<br>
         View status<br>
         Provision new units<br>
         Change the configuration`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.appUsersLink),
        content: `Manage Mobile Application Users<br>
         View, Search, and Sort Users<br>
         Create New Users (Single or Bulk)<br>
         Change User Level Notification Filters<br>
         Generate Organization QR Code`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.connectionsLink),
        content: `Manage organization Connections<br>
              Create, View and Edit<br>
              Connections with other organizations`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.eventsLink),
        content: `Manage Events<br>
              View, Search,<br>
              Export and Delete`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.accountsLink),
        content: `Manage Accounts<br>
              View, Edit and Add`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.kabobMenu),
        content: `Access additional screens`,
        params: defaultParams,
        before: openAndLockMenu,
      },
      {
        target: selector(tourStepsTargets.downloadsLink),
        content: `Download and approve Event Exports`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.instructionsLink),
        content: `Add and Modify Instructions pertaining to Event types`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.trainingLink),
        content: `Generate Training Events for Owned or Connected Devices`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.trainingScenariosLink),
        content: `Configure Training Scenarios composed of
                 Training Event Templates to simulate real-world situations`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.nonRMSEventsLink),
        content: `Generate Banner and Email Events for Connected Accounts`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.configurationLink),
        content: `View and Modify Organization contact information, retention settings and notification filters`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.conditionalUsersLink),
        content: `Configure conditional access settings for Mobile Application Users`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.subgroupsLink),
        content: `Configure subgroups of users for dispatches`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.summaryLink),
        content: `View and Modify Organization Summary on the Site/Building/Room levels`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.dispatcherLink),
        content: `Dispatch Patrol Users to Events and Connections`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.cadLink),
        content: `Modify Templates for CAD Backend Subscribers`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.ssoLink),
        content: `Guided setup for organization Single Sign-On (SSO) configuration`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.viewYourAccountLink),
        content: `View and Modify Contact Information for your Account`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.changePasswordLink),
        content: `Change the Password of the current Account`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.supportLink),
        content: `For additional help, click this link to send an email to SECURE help desk or use the SECURE Chat Bot`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.logoutLink),
        content: `Sign out of the current Account`,
        params: placementLeftParams,
      },
      {
        target: selector(tourStepsTargets.welcomeLink),
        content: `Click here to return to the Welcome screen`,
        params: {
          placement: 'bottom',
          enableScrolling: false,
        },
        before: closeAndUnlockMenu,
      },
    ],
  };

  const ownerHiddenList = [
    selector(tourStepsTargets.dispatcherLink),
    selector(tourStepsTargets.cadLink),
    selector(tourStepsTargets.appUsersLink),
  ];
  const consumerHiddenList = [
    selector(tourStepsTargets.devicesLink),
    selector(tourStepsTargets.summaryLink),
  ];
  const imageTourObj = {
    componentName: 'mainNavigation',
    newSteps: [
      {
        target: selector(tourStepsTargets.welcomeLink),
        content: `Welcome to the Sentry-SECURE Portal<br><br>
          Sentry-SECURE platform provides situational awareness for connected RMS Owner and Consumer Organizations in a cloud platform<br><br>
          Help prompts like this one are located on all tabs and will provide contextual information`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.welcomeLink),
        content: `The Image Viewer account only has access to view Event Media.<br><br>
          Copy the Event Media Link into the browser to view the media. The link will be provided by the organization admin.`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.logoutLink),
        content: `Sign out of the current Account`,
        params: placementLeftParams,
      },
    ],
  };

  const dispatcherTourObj = {
    componentName: 'mainNavigation',
    newSteps: [
      {
        target: selector(tourStepsTargets.welcomeLink),
        content: `Welcome to the Sentry-SECURE Portal<br><br>
          Sentry-SECURE platform provides situational awareness for connected RMS Owner and Consumer Organizations in a cloud platform<br><br>
          Help prompts like this one are located on all tabs and will provide contextual information`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.cadLink),
        content: `The Dispatcher account only has access to the Consumer Dispatcher tab.<br><br>
          Click here to switch to the Dispatcher Tab.<br><br>
          The Dispatcher tab allows Patrol Users to be dispatched to active Events or Connections`,
        params: defaultParams,
      },
      {
        target: selector(tourStepsTargets.logoutLink),
        content: `Sign out of the current Account`,
        params: placementLeftParams,
      },
    ],
  };

  // Determine which hidden list to use based on user role
  let checkHiddenList;
  if (isConsumer) {
    checkHiddenList = consumerHiddenList;
  } else if (isOwner) {
    checkHiddenList = ownerHiddenList;
  }

  // Filter out hidden steps
  let trimmedSteps = tourObj.newSteps;
  for (let i = 0; i < checkHiddenList.length; ++i) {
    for (let j = 0; j < trimmedSteps.length; ++j) {
      if (trimmedSteps[j].target == checkHiddenList[i]) {
        trimmedSteps.splice(j, 1);
      }
    }
  }

  // Return appropriate tour steps based on user type
  if (isImageViewer) {
    return imageTourObj.newSteps;
  } else if (isDispatcher) {
    return dispatcherTourObj.newSteps;
  } else {
    return trimmedSteps;
  }
}
